import React, { Suspense, useCallback } from 'react';
import { ICampaign, INewCartEntry, INewCartShipment } from 'pages/NewCart/ICart';
import CartList from '../CartList';
import ShipmentCardFooter from '../ShipmentCardFooter';
import { isExpressShipment, isFoodShipment } from 'pages/NewCart/helpers/shipment.helper';

import * as S from './ShipmentCard.style';
import { LOGIN_TO_NEW_CHECKOUT_PAGE } from 'config/Urls.config';
import useSustainabilityConsent from 'pages/Cart/hooks/useSustainabilityConsent.hook';
import useAddressFormOpen from 'pages/Cart/hooks/useAddressFormOpen.hook';
import useGoToCheckoutScreen from 'pages/Cart/hooks/useGoToCheckoutScreen.hook';
import { getUserTokenFromCookie } from 'utils/cookies.util';
import { initStorageManager } from 'utils/customStorage.util';
import USER_CONFIG from 'config/User.config';
import Constants from 'config/constants';
import useOrderSummary from 'common/OrderSummary/hooks/useOrderSummary.hook';
import OOSItems from '../OOSItems';
import useOOSItems from 'pages/NewCart/hooks/useOOSItems';
import TrackerCheckout from 'utils/Tracker.checkout';

import useEmirateAddress from 'pages/Cart/hooks/useEmirateAddress.hook';
import UpdateEmiratePopup from '../UpdateEmiratePopup';
import useCartBundling from 'hooks/useCartBundling';
import { transformBundleEntries } from 'pages/Cart/redux/actions/newCart.actions';

const storage = initStorageManager(() => localStorage);

const AddAddress = React.lazy(() =>
  import('@mafc/common').then(module => ({ default: module.AddAddress }))
);

const UserConsentBags = React.lazy(() =>
  import('@mafc/common').then(module => ({ default: module.UserConsentBags }))
);

const ShipmentCard: React.FC<{
  isNowUnavailableHour: boolean;
  shipment: INewCartShipment;
  totalShipments: number;
  isOOSSubstitutionEnabled: boolean;
}> = ({ isNowUnavailableHour = false, shipment, totalShipments, isOOSSubstitutionEnabled }) => {
  const isCartPromoBundleEnabled = useCartBundling();
  const enteriesWithBundle = shipment.entries.some(item => 'bundleId' in item);
  const bundleEntries =
    enteriesWithBundle && isCartPromoBundleEnabled
      ? (transformBundleEntries(shipment) as INewCartEntry[][])
      : [];

  const { entries, shipmentType, oosEntries, campaigns } = shipment;

  const isExpress = isExpressShipment(shipment.shipmentType);
  const isFood = isFoodShipment(shipment.shipmentType);

  const {
    shouldOpenTheConsent,
    isConsentFormOpen,
    closeConsentForm,
    setUserConsent,
    isSustainabilityConsentRequired,
    openConsentForm,
    isSingleUseBagsSupported,
  } = useSustainabilityConsent();
  const {
    isAddressFormOpen,
    onCloseAddressForm,
    isValidAddress,
    openAddressForm,
    onAddressSuccess,
    isLocationCx,
    selectedAddress,
    isSelectedAddressPresent,
  } = useAddressFormOpen();

  const {
    isEmiratePopupOpen,
    addressHasEmirateField,
    openEmiratePopup,
    closeEmiratePopup,
    isEmirateEnabled,
  } = useEmirateAddress(selectedAddress);

  const { isOOSInfoOpen, deleteAllItems, openOOSInfo, closeOOSInfo } = useOOSItems(oosEntries);
  const { goToCheckoutScreen } = useGoToCheckoutScreen();
  // todo will create another component in future
  const userConsentCbkHandler = (isUserConsent: boolean) => {
    setUserConsent(isUserConsent);
    checkoutHandler();
  };
  const addressSuccessCbk = () => {
    onAddressSuccess();
    if (shouldOpenTheConsent && (isExpress || isFood)) {
      openConsentForm();
      return false;
    }
    checkoutHandler();
  };
  const { totalUnitCount } = useOrderSummary();
  const onProceedToCheckout = () => {
    if (!getUserTokenFromCookie()) {
      if (!!totalUnitCount) {
        storage.setItem(Constants.guestCartQuantity, JSON.stringify(totalUnitCount));
      }
      document.location.replace(LOGIN_TO_NEW_CHECKOUT_PAGE);
      return false;
    }
    if ((isSelectedAddressPresent || !isValidAddress()) && USER_CONFIG.isLoggedIn) {
      openAddressForm();
      return false;
    }

    if (isEmirateEnabled && !addressHasEmirateField) {
      openEmiratePopup();
      return false;
    }

    if (oosEntries.length > 0) {
      openOOSInfo();
      return false;
    }
    if (shouldOpenTheConsent && (isExpress || isFood)) {
      openConsentForm();
      return false;
    }
    checkoutHandler();
  };

  const onUpdateEmiratePopup = useCallback(() => {
    closeEmiratePopup();
    openAddressForm();
  }, [closeEmiratePopup, openAddressForm]);

  const checkoutHandler = () => {
    goToCheckoutScreen({
      shipmentType: shipment.identifier,
      isNewJourney: true,
    });
  };

  const removeItemsAndProceedCheckout = () => {
    deleteAllItems();
    checkoutHandler();
  };

  const mapCampaigns = (shipmentEntries: INewCartEntry[]) => {
    if (!shipmentEntries || !shipmentEntries.length) {
      return [];
    }
    return shipmentEntries.map(entry => {
      const { campaignId } = entry;
      const campaign = (campaigns
        ? campaigns.find(item => item.id === campaignId)
        : {}) as ICampaign;
      return {
        ...entry,
        ...(campaign ? { campaign, bundleText: campaign.shortText } : {}),
      };
    });
  };

  return (
    <>
      <Suspense fallback={<></>}>
        {/* user contsent drawer */}
        {isSustainabilityConsentRequired && isSingleUseBagsSupported && isConsentFormOpen && (
          <UserConsentBags
            isOpen={isConsentFormOpen}
            isFormVisible
            callBack={userConsentCbkHandler}
            close={closeConsentForm}
            isShowCloseButton
          />
        )}
        {isAddressFormOpen && (
          <AddAddress
            isOpen={true}
            editInfo={selectedAddress}
            onClose={onCloseAddressForm}
            isGoogleMapVisible={isLocationCx}
            onSuccess={addressSuccessCbk}
          />
        )}
        {isOOSInfoOpen && (
          <OOSItems
            close={closeOOSInfo}
            isOpen={isOOSInfoOpen}
            oosEntries={mapCampaigns(oosEntries)}
            removeItemsAndProceedCheckout={removeItemsAndProceedCheckout}
          />
        )}
        {isEmiratePopupOpen && (
          <UpdateEmiratePopup isOpen={isEmiratePopupOpen} onUpdate={onUpdateEmiratePopup} />
        )}
      </Suspense>

      <S.ShipmentCard>
        <CartList
          entries={mapCampaigns(entries)}
          oosEntries={mapCampaigns(oosEntries)}
          shipmentType={shipmentType}
          isOOSSubstitutionEnabled={isOOSSubstitutionEnabled}
          bundleEntries={bundleEntries}
        />

        {totalShipments > 1 ? (
          <ShipmentCardFooter
            shipment={shipment}
            checkoutHandler={onProceedToCheckout}
            disableCheckout={
              !shipment.movMet ||
              (isExpress && isNowUnavailableHour) ||
              (shipment.subTotal && shipment.subTotal.value === 0)
            }
          />
        ) : null}
      </S.ShipmentCard>
    </>
  );
};

export default ShipmentCard;
